import { useEffect, useRef, useState } from 'react';
import type { ProductEventDataList } from 'corgi-types';

import type { ProductViewTrackingArticle } from './productViewEvent';
import { dispatchProductViewEvent } from './productViewEvent';

interface UseObserveArticlesArgs {
  articles: ProductViewTrackingArticle[];
  listType: ProductEventDataList;
  dataTestId?: string;
  debounce?: number;
}

const transformTrackingArticle = (articles: ProductViewTrackingArticle[]) =>
  articles.map(({ tracking, sku, sponsored, dyDecisionId, dySlotId, hasMinPrice }, index) => ({
    tracking,
    sku,
    sponsored,
    dyDecisionId,
    dySlotId,
    index,
    hasMinPrice,
  }));

export const useObserveArticles = ({
  articles,
  listType,
  dataTestId = 'product-item',
  debounce = 0,
}: UseObserveArticlesArgs) => {
  const observedSkusRef = useRef<Set<string>>(undefined);
  const intersectionObserverRef = useRef<IntersectionObserver>(undefined);
  const productsSkuNodeMapRef = useRef<Map<string, HTMLDivElement>>(undefined);
  const articlesRef = useRef<ProductViewTrackingArticle[]>(articles);

  const [articlesToTrack, setArticlesToTrack] = useState<ProductViewTrackingArticle[]>([]);
  const trackedCountRef = useRef(0);

  const handleTracking = (articlesList: ProductViewTrackingArticle[]) => {
    if (articlesList.length) {
      const currentOffset = trackedCountRef.current;
      dispatchProductViewEvent(transformTrackingArticle(articlesList), listType, currentOffset);
      trackedCountRef.current += articlesList.length;
    }
  };

  useEffect(() => {
    if (debounce) {
      const handler = setTimeout(() => {
        handleTracking(articlesToTrack);
      }, debounce);

      return () => clearTimeout(handler);
    } else {
      handleTracking(articlesToTrack);
    }
  }, [articlesToTrack, debounce]);

  useEffect(() => {
    let isMounted = true;
    observedSkusRef.current = new Set<string>();
    productsSkuNodeMapRef.current = new Map();
    intersectionObserverRef.current = new IntersectionObserver(entries => {
      const intersectedSkus: string[] = [];
      entries.forEach(entry => {
        if (entry.isIntersecting || entry.intersectionRatio > 0) {
          const target = entry.target as HTMLDivElement;
          const dataset = target?.dataset;
          if (dataset?.sku) {
            intersectedSkus.push(dataset.sku);
            intersectionObserverRef.current?.unobserve(target);
          }
        }
      });
      const currentArticles = articlesRef.current;
      const viewArticlesTracking = currentArticles.filter(({ sku }) =>
        intersectedSkus.includes(sku)
      );
      if (!isMounted) {
        return;
      }
      setArticlesToTrack(viewArticlesTracking);
    });

    return () => {
      isMounted = false;
      intersectionObserverRef.current?.disconnect();
    };
  }, []);

  useEffect(() => {
    articlesRef.current = articles;
    const observedSkus = new Set(observedSkusRef.current);
    const items = Array.from(
      document.querySelectorAll<HTMLDivElement>(`[data-testid="${dataTestId}"]`)
    );

    const newProductsSkuNodeMap = new Map<string, HTMLDivElement>();
    for (const item of items) {
      const sku = item.dataset.sku ?? '';
      newProductsSkuNodeMap.set(sku, item);
      if (observedSkus.has(sku)) {
        observedSkus.delete(sku);
      } else {
        intersectionObserverRef.current?.observe(item);
        observedSkusRef.current?.add(sku);
      }
    }
    for (const unusedSku of observedSkus) {
      observedSkusRef.current?.delete(unusedSku);
      const removedProductNode = productsSkuNodeMapRef.current?.get(unusedSku);
      if (removedProductNode) {
        intersectionObserverRef.current?.unobserve(removedProductNode);
      }
    }
    productsSkuNodeMapRef.current = newProductsSkuNodeMap;
  }, [articles]);
};
